import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PublicLayout } from '../components/PublicLayout';
import { Alert, Button, Spinner } from '../components/ui';
import { ApiError, api } from '../services/api';
import { useSession } from '../session';
import type { InvitationPreview } from '../types';

type Preview = { status: 'loading' } | { status: 'ok'; preview: InvitationPreview } | { status: 'invalid' };

export function InvitePage() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { state: session, user, setUser, signOut } = useSession();
  const [preview, setPreview] = useState<Preview>({ status: 'loading' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .previewInvitation(token)
      .then((p) => setPreview({ status: 'ok', preview: p }))
      .catch(() => setPreview({ status: 'invalid' }));
  }, [token]);

  if (preview.status === 'loading' || session.status === 'loading') {
    return (
      <PublicLayout title={t('invite.title')}>
        <Spinner />
      </PublicLayout>
    );
  }
  if (preview.status === 'invalid') {
    return (
      <PublicLayout title={t('invite.invalid')} subtitle={t('invite.invalidHint')}>
        <Link to={user ? '/' : '/login'}>
          <Button className="w-full" variant="secondary">
            {user ? t('app.goHome') : t('auth.signIn')}
          </Button>
        </Link>
      </PublicLayout>
    );
  }
  const p = preview.preview;
  const lead = p.inviter ? t('invite.lead', { inviter: p.inviter, organization: p.organization, role: t(`role.${p.role}`) }) : t('invite.leadNoInviter', { organization: p.organization, role: t(`role.${p.role}`) });

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.acceptInvitation(token);
      setUser(updated);
      navigate('/organization', { replace: true, state: { joined: p.organization } });
    } catch (err) {
      if (err instanceof ApiError && err.is('invitation_mismatch')) setError(t('invite.mismatch', { current: user?.email ?? '', email: p.email }));
      else if (err instanceof ApiError && err.is('owner_must_transfer')) setError(t('invite.ownerMustTransfer'));
      else if (err instanceof ApiError && err.is('already_member')) setError(t('invite.alreadyMember'));
      else if (err instanceof ApiError && err.status === 404) setPreview({ status: 'invalid' });
      else setError(t('app.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout title={t('invite.title')} subtitle={lead}>
      <p className="font-mono text-[11.5px] text-faint">{t('invite.sentTo', { email: p.email })}</p>
      {error && <Alert kind="error">{error}</Alert>}
      {!user && (
        <>
          {/* returnTo: once the account exists, the guard of /signup (App.tsx) is what moves the page on. */}
          <Link to="/signup" state={{ invitation: { token, preview: p }, returnTo: '/organization' }}>
            <Button className="w-full">{t('invite.createAccount')}</Button>
          </Link>
          <Link to="/login" state={{ returnTo: `/invite/${token}` }}>
            <Button className="w-full" variant="secondary">
              {t('invite.signInToAccept')}
            </Button>
          </Link>
        </>
      )}
      {user && user.email.toLowerCase() === p.email.toLowerCase() && (
        <Button className="w-full" onClick={() => void accept()} disabled={busy}>
          {busy ? t('invite.accepting') : t('invite.accept', { organization: p.organization })}
        </Button>
      )}
      {user && user.email.toLowerCase() !== p.email.toLowerCase() && (
        <>
          <Alert kind="warn">{t('invite.mismatch', { current: user.email, email: p.email })}</Alert>
          <Button
            className="w-full"
            variant="secondary"
            onClick={() => {
              void signOut();
            }}
          >
            {t('app.signOut')}
          </Button>
        </>
      )}
    </PublicLayout>
  );
}
